import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import type { UserRole } from '@shift/contracts';
import { useSessionStore, CLIENT_APP_ROLES } from '../entities/session/session.store.js';
import { decideNavigation } from './navigation-policy.js';

declare module 'vue-router' {
  interface RouteMeta {
    /** Route is reachable without a session. */
    public?: boolean;
    /** Roles allowed here; defaults to the app's client roles. */
    roles?: readonly UserRole[];
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: () => import('../layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('../pages/LoginPage.vue'),
        meta: { public: true },
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('../pages/RegisterPage.vue'),
        meta: { public: true },
      },
      {
        // A firm registers before it has an account, so this sits with the
        // other pages somebody reaches without one.
        path: 'pentru-firme',
        name: 'partner-signup',
        component: () => import('../pages/PartnerSignupPage.vue'),
        meta: { public: true },
      },
    ],
  },
  {
    path: '/',
    component: () => import('../layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('../pages/HomePage.vue'),
      },
      {
        path: 'addresses',
        name: 'addresses',
        component: () => import('../pages/AddressesPage.vue'),
      },
      {
        path: 'book',
        name: 'book',
        component: () => import('../pages/BookJobPage.vue'),
      },
      {
        path: 'help',
        name: 'help',
        component: () => import('../pages/HelpPage.vue'),
        // Readable before anyone has an account: somebody deciding whether to
        // trust this platform reads it first.
        meta: { public: true },
      },
      {
        path: 'cards',
        name: 'cards',
        component: () => import('../pages/CardsPage.vue'),
      },
      {
        path: 'company',
        name: 'company',
        component: () => import('../pages/CompanyPage.vue'),
      },
      {
        path: 'referrals',
        name: 'referrals',
        component: () => import('../pages/ReferralsPage.vue'),
      },
      {
        path: 'jobs/:jobId',
        name: 'job-detail',
        component: () => import('../pages/JobDetailPage.vue'),
      },
    ],
  },
  {
    // Where a colleague lands from an invitation email. Public: they may have
    // no account yet, and the whole point of the link is that it works before
    // they do.
    path: '/invitations/:token',
    name: 'organisation-invitation',
    component: () => import('../pages/JoinCompanyPage.vue'),
    meta: { public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../pages/NotFoundPage.vue'),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ left: 0, top: 0 }),
});

/**
 * The guard performs what `decideNavigation` decides; the reasoning lives there
 * so it can be tested without a browser.
 */
router.beforeEach(async (to) => {
  const session = useSessionStore();

  // Before deciding anything. The router begins its first navigation the moment
  // it is installed, which is before `main.ts` can restore the session — so
  // without this the guard judged an empty session on every reload and sent a
  // signed-in user to the login page, while the restore completed a moment
  // later against a page they were no longer on.
  await session.whenRestored();

  const decision = decideNavigation({
    routeName: typeof to.name === 'string' ? to.name : undefined,
    isPublic: to.meta.public === true,
    fullPath: to.fullPath,
    isAuthenticated: session.isAuthenticated,
    roles: session.roles,
    allowedRoles: to.meta.roles ?? CLIENT_APP_ROLES,
  });

  if (decision.kind === 'allow') return true;

  // Ends it at the server too: the refresh cookie would otherwise restore the
  // account on the next load, and the guard would end it again on every visit.
  if (decision.kind === 'end-session') {
    await session.signOut();
    // No target means the request may continue now that the session is gone —
    // the login page, which would otherwise be redirected to itself.
    return decision.to ?? true;
  }

  return decision.to;
});
